import { useQuery } from '@tanstack/react-query'
import { patientsApi, type PatientEncounter } from '@/api/patientsApi'
import { useTenant } from '@/providers/TenantProvider'
import { sortEncounters } from '@/lib/encounters'

/**
 * A patient's Epic visit history.
 *
 * Two surfaces use it, and both are popups: the Patient Visit History tab of
 * the Patient History dialog, and the Patient Visit History dialog reached from
 * the patient detail page (z8nrz7e0rb).
 *
 * `selectedTenantId` is in the query key rather than relied on through a
 * remount: an MRN is per tenant, so the same `mrn` names a different person (or
 * nobody) after a switch, and a cached list keyed on the MRN alone would paint
 * the previous tenant's visits. See the tenancy notes in CLAUDE.md.
 *
 * `enabled` is a caller's decision, not a fixed rule, and today both callers
 * pass their dialog's `open` state: nothing fetches a patient's admissions
 * until someone asks to see them.
 *
 * Encounters change only when Epic sends an ADT, so the default 5-min
 * `staleTime` is generous and no refetch is wired.
 */
export function usePatientEncounters(
  mrn: string | null | undefined,
  /** Whether this surface wants the data yet. See the note above. */
  enabled: boolean,
) {
  const { selectedTenantId } = useTenant()
  return useQuery<PatientEncounter[]>({
    queryKey: ['patient-encounters', selectedTenantId, mrn],
    enabled: enabled && !!mrn && !!selectedTenantId,
    queryFn: () => patientsApi.encounters(mrn as string),
    // Ordered here as well as server-side so the table cannot disagree with the
    // endpoint, and so a stamp-less visit has a defined position either way.
    select: sortEncounters,
  })
}
